/**
 * De lijstkolom van Reviews (ontwerp v4 §7, mock "s-reviews").
 *
 * Zes tegels: Reageren (standaard), Alles, WebwinkelKeur, Google,
 * Negatief, Positief. Dezelfde klassen als de kaartenkolom van Vandaag,
 * zodat het visueel één kolom is; de prop om die kolom zelf te
 * hergebruiken staat in `VERZOEK.md`.
 *
 * De kaart toont de bron, het cijfer in de schaal van dát platform
 * ("6 van 10" of "5 sterren"), de naam en de eerste regels van de
 * review. Geen knoppen: de kaart is de knop.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { werkplekReviewsApi, type ReviewKaart } from "@/lib/werkplek-api";

import { dagkop, dagsleutel, kaarttijd } from "../Vandaag/tijd";
import "../Vandaag/vandaag.css";
import { IcoonSter } from "../Retouren/pictogrammen-retour";
import "../Retouren/retouren.css";

export const REVIEWFILTERS = [
  { sleutel: "reageren", label: "Reageren" },
  { sleutel: "alles", label: "Alles" },
  { sleutel: "webwinkelkeur", label: "WebwinkelKeur" },
  { sleutel: "google", label: "Google" },
  { sleutel: "negatief", label: "Negatief" },
  { sleutel: "positief", label: "Positief" },
] as const;

export type ReviewFilterSleutel = (typeof REVIEWFILTERS)[number]["sleutel"];

const LEEGTEKST: Record<string, string> = {
  reageren: "Geen enkele review wacht op een reactie van jou.",
  alles: "Er zijn nog geen reviews opgehaald.",
  webwinkelkeur: "Er staan geen WebwinkelKeur-reviews in dit venster.",
  google: "Er staan geen Google-reviews in dit venster.",
  negatief: "Geen enkele review in dit venster is negatief.",
  positief: "Geen enkele review in dit venster is positief.",
};

interface Props {
  filter: ReviewFilterSleutel;
  opFilter: (welke: ReviewFilterSleutel) => void;
  zoek: string;
  gekozenReviewId: string | null;
  opKiezen: (reviewId: string) => void;
  /** De bovenste kaart, zodra de lijst er is (JP, punt 1 van 22-09). */
  opEersteKaart?: (reviewId: string | null) => void;
}

function KaartRij({
  kaart,
  gekozen,
  opKiezen,
  verwijzing,
}: {
  kaart: ReviewKaart;
  gekozen: boolean;
  opKiezen: (reviewId: string) => void;
  verwijzing: (el: HTMLButtonElement | null) => void;
}) {
  return (
    <button
      type="button"
      ref={verwijzing}
      className="wp-kaart"
      data-review={kaart.review_id}
      data-afgerond={kaart.afgerond ? "ja" : "nee"}
      aria-current={gekozen}
      onClick={() => opKiezen(kaart.review_id)}
    >
      <span className="wp-kaart-r1">
        <span className="wp-chip wp-chip-grijs">{kaart.bron_label}</span>
        <span
          className={`wp-chip ${kaart.negatief ? "wp-chip-risico" : "wp-chip-groen"}`}
          data-score
        >
          <span className="wp-sterren">
            <IcoonSter maat={11} vol={!kaart.negatief} />
          </span>
          {kaart.score_label}
        </span>
        <span className="wp-kaart-naam">{kaart.naam}</span>
      </span>
      <span className="wp-kaart-tijd">
        {kaart.moment ? kaarttijd(kaart.moment) : "geen datum"}
      </span>
      <span className="wp-kaart-wat">{kaart.tekst}</span>
      <span className="wp-kaart-waarom">{kaart.waarom}</span>
    </button>
  );
}

export function ReviewsKolom({
  filter,
  opFilter,
  zoek,
  gekozenReviewId,
  opKiezen,
  opEersteKaart,
}: Props) {
  const knoppen = useRef<Map<string, HTMLButtonElement>>(new Map());

  const vraag = useQuery({
    queryKey: ["werkplek", "reviews", filter, zoek],
    queryFn: () => werkplekReviewsApi.lijst({ filter, zoek, limiet: 100 }),
  });

  const kaarten = useMemo(() => vraag.data?.kaarten ?? [], [vraag.data]);
  const tellers = vraag.data?.tellers ?? {};

  // Leeg actief filter terwijl er wel reviews zijn: één keer uitwijken
  // naar Alles (JP, punt 1 van 22-09).
  const uitgeweken = useRef(false);
  useEffect(() => {
    if (vraag.isLoading || zoek.trim()) return;
    if (filter === "alles" || uitgeweken.current) return;
    if (kaarten.length > 0) return;
    if ((tellers.alles ?? 0) > 0) {
      uitgeweken.current = true;
      opFilter("alles");
    }
  }, [vraag.isLoading, zoek, filter, kaarten.length, tellers.alles, opFilter]);

  useEffect(() => {
    if (vraag.isLoading) return;
    opEersteKaart?.(kaarten[0]?.review_id ?? null);
  }, [vraag.isLoading, kaarten, opEersteKaart]);

  const spring = useCallback(
    (richting: 1 | -1) => {
      if (kaarten.length === 0) return;
      const nu = kaarten.findIndex((k) => k.review_id === gekozenReviewId);
      const volgende =
        nu === -1
          ? 0
          : Math.min(Math.max(nu + richting, 0), kaarten.length - 1);
      const doel = kaarten[volgende];
      if (!doel) return;
      opKiezen(doel.review_id);
      knoppen.current.get(doel.review_id)?.scrollIntoView({ block: "nearest" });
    },
    [kaarten, gekozenReviewId, opKiezen],
  );

  useEffect(() => {
    function toets(gebeurtenis: KeyboardEvent) {
      const doel = gebeurtenis.target as HTMLElement | null;
      if (
        doel &&
        (doel.tagName === "INPUT" ||
          doel.tagName === "TEXTAREA" ||
          doel.isContentEditable)
      ) {
        return;
      }
      if (gebeurtenis.key === "ArrowDown" || gebeurtenis.key === "j") {
        gebeurtenis.preventDefault();
        spring(1);
      } else if (gebeurtenis.key === "ArrowUp" || gebeurtenis.key === "k") {
        gebeurtenis.preventDefault();
        spring(-1);
      }
    }
    window.addEventListener("keydown", toets);
    return () => window.removeEventListener("keydown", toets);
  }, [spring]);

  let vorigeDag = "";

  return (
    <section aria-label="Reviews">
      <div className="wp-filters" role="group" aria-label="Filters">
        {REVIEWFILTERS.map((tegel) => (
          <button
            key={tegel.sleutel}
            type="button"
            className="wp-filter"
            aria-pressed={filter === tegel.sleutel}
            data-filter={tegel.sleutel}
            onClick={() => opFilter(tegel.sleutel)}
          >
            <span className="wp-filter-label">{tegel.label}</span>
            {tegel.sleutel in tellers ? <b>{tellers[tegel.sleutel]}</b> : null}
          </button>
        ))}
      </div>

      {vraag.isLoading ? (
        <div className="wp-leeg">Bezig met laden…</div>
      ) : vraag.isError ? (
        <div className="wp-leeg" role="alert">
          De reviews zijn niet op te halen. Ververs de pagina; blijft dit
          staan, dan is de API niet bereikbaar.
        </div>
      ) : kaarten.length === 0 ? (
        <div className="wp-leeg">
          {LEEGTEKST[filter] ?? "Geen reviews in dit filter."}
        </div>
      ) : (
        kaarten.map((kaart) => {
          const dag = kaart.moment ? dagsleutel(kaart.moment) : "";
          const kop =
            kaart.moment && dag !== vorigeDag ? dagkop(kaart.moment) : null;
          vorigeDag = dag;
          return (
            <div key={kaart.review_id}>
              {kop ? <div className="wp-datum">{kop}</div> : null}
              <KaartRij
                kaart={kaart}
                gekozen={kaart.review_id === gekozenReviewId}
                opKiezen={opKiezen}
                verwijzing={(el) => {
                  if (el) knoppen.current.set(kaart.review_id, el);
                  else knoppen.current.delete(kaart.review_id);
                }}
              />
            </div>
          );
        })
      )}
    </section>
  );
}
